/**
 * Comment thread + status timeline for one action item, backed by action_item_updates
 * (PRD_ACTION_ITEMS_OVERHAUL Phase 2). Shared by the Action items page and the meeting's
 * Actions tab. Everything written here (and by the email update page, and by the meeting AI)
 * is what the nudge/recap follow-through sections render - one trail, many surfaces.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, MessageSquare, Sparkles, CheckCircle2, RotateCcw, CircleDot } from 'lucide-react';
import { track } from '@/lib/analytics';

export type ActionItemUpdate = {
  id: string;
  action_item_id: string;
  author_user_id: string | null;
  author_email: string | null;
  author_name: string | null;
  kind: 'comment' | 'status_change';
  body: string | null;
  old_status: string | null;
  new_status: string | null;
  source: 'app' | 'email' | 'meeting_ai';
  created_at: string;
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
  dropped: 'Archived',
};

function authorLabel(u: ActionItemUpdate): string {
  if (u.source === 'meeting_ai') return 'Upstand (from the meeting)';
  return u.author_name?.trim() || u.author_email?.trim() || 'A teammate';
}

function whenLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ActionItemThread({
  actionItemId,
  currentUserId,
  currentUserName,
  currentUserEmail,
  onCountChange,
}: {
  actionItemId: string;
  currentUserId: string | null;
  currentUserName?: string | null;
  currentUserEmail?: string | null;
  /** Lets the host row keep its comment-count badge in sync without re-querying. */
  onCountChange?: (count: number) => void;
}) {
  const [updates, setUpdates] = useState<ActionItemUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from('action_item_updates')
      .select('*')
      .eq('action_item_id', actionItemId)
      .order('created_at', { ascending: true });
    if (qErr) {
      setError(qErr.message);
    } else {
      const rows = (data || []) as ActionItemUpdate[];
      setUpdates(rows);
      onCountChange?.(rows.length);
    }
    setLoading(false);
  }, [actionItemId, onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const body = comment.trim();
    if (!body || !currentUserId) return;
    setSaving(true);
    setError(null);
    const { error: insErr } = await supabase.from('action_item_updates').insert({
      action_item_id: actionItemId,
      author_user_id: currentUserId,
      author_name: currentUserName || null,
      author_email: currentUserEmail || null,
      kind: 'comment',
      body,
      source: 'app',
    });
    if (insErr) {
      setError(insErr.message);
    } else {
      setComment('');
      track('action_item_commented', { source: 'app' });
      await load();
    }
    setSaving(false);
  };

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading updates...
        </div>
      ) : updates.length === 0 ? (
        <p className="text-xs text-muted-foreground">No updates yet. Add the first progress note.</p>
      ) : (
        <ul className="space-y-2">
          {updates.map((u) => {
            const isStatus = u.kind === 'status_change';
            const toDone = u.new_status === 'done';
            // done -> dropped is an archive, not a reopen: only a move back to a live status counts.
            const reopened =
              isStatus &&
              u.old_status === 'done' &&
              (u.new_status === 'open' || u.new_status === 'in_progress');
            const Icon = u.source === 'meeting_ai' ? Sparkles : isStatus ? (toDone ? CheckCircle2 : reopened ? RotateCcw : CircleDot) : MessageSquare;
            const iconClass =
              u.source === 'meeting_ai'
                ? 'text-primary'
                : toDone
                  ? 'text-emerald-700'
                  : 'text-muted-foreground';
            return (
              <li key={u.id} className="flex gap-2 text-sm">
                <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconClass}`} />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{authorLabel(u)}</span>
                    {isStatus ? (
                      <>
                        {' '}
                        set status to {STATUS_LABEL[u.new_status || ''] || u.new_status}
                        {u.old_status ? ` (was ${STATUS_LABEL[u.old_status] || u.old_status})` : ''}
                      </>
                    ) : null}
                    {' · '}
                    {whenLabel(u.created_at)}
                  </p>
                  {u.body ? <p className="whitespace-pre-wrap break-words text-sm text-foreground">{u.body}</p> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {currentUserId ? (
        <div className="flex items-start gap-2 pt-1">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a progress update..."
            rows={1}
            maxLength={2000}
            className="min-h-[36px] flex-1 text-sm"
          />
          <Button size="sm" onClick={submit} disabled={saving || !comment.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post'}
          </Button>
        </div>
      ) : null}
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}
